import { useEffect } from "react";

import { useApolloClient } from "@apollo/client/react";
import { Route, Routes, useParams } from "react-router-dom";

import Main from "@/components/app/Main";
import Loading from "@/containers/Loading";
import { ReactLazyLoading } from "@/containers/ReactLoading";
import { SessionCustomerDocument } from "@/gql/graphql";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { isGJUDemo } from "@/new_utils";
import { sessionSelector } from "@/store/sessionReducer";
import { setCurrentCustomerAndProject } from "@/store/sessionReducer";

const CustomerContactsInterface = ReactLazyLoading(() => import("@/components/CustomerContacts"));
const AdminInterface = ReactLazyLoading(() => import("@/components/admin"));
const ProfileInterface = ReactLazyLoading(() => import("@/components/admin/users/profile"));
const ContactsInterface = ReactLazyLoading(() => import("@/components/contacts"));
const TodosInterface = ReactLazyLoading(() => import("@/components/todos"));
const JobsInterface = ReactLazyLoading(() => import("@/components/jobs"));
const CalendarInterface = ReactLazyLoading(() => import("@/components/calendar"));
const StatsInterface = ReactLazyLoading(() => import("@/components/stats"));
const InvoicesInterface = ReactLazyLoading(() => import("@/components/invoices"));
const ProjectInterface = ReactLazyLoading(() => import("@/components/project"));
const KnowledgebaseInterface = ReactLazyLoading(() => import("@/components/knowledgebase"));
const KnowledgebaseNgInterface = ReactLazyLoading(() => import("@/components/KnowledgebaseNg"));
const MessagesInterface = ReactLazyLoading(() => import("@/components/messages"));
const StaffInterface = ReactLazyLoading(() => import("@/components/staff"));
const AcdInterface = ReactLazyLoading(() => import("@/components/acd"));
const RosterInterface = ReactLazyLoading(() => import("@/components/roster"));
const DocumentsInterface = ReactLazyLoading(() => import("@/components/documents"));
const TicketsInterface = ReactLazyLoading(() => import("@/components/tickets"));
const PunchlistInterface = ReactLazyLoading(() => import("@/components/punchlist"));
const MailsInterface = ReactLazyLoading(() => import("@/components/mails"));
const InventoryInterface = ReactLazyLoading(() => import("@/components/inventory"));
const CustomerFeedbackInterface = ReactLazyLoading(() => import("@/components/CustomerFeedback"));
const HotSeatInterface = ReactLazyLoading(() => import("@/components/HotSeat"));
const ProjectNewsInterface = ReactLazyLoading(() => import("@/components/ProjectNews"));
const FileExchange = ReactLazyLoading(() => import("@/components/FileExchange"));
const ReminderNotifications = ReactLazyLoading(() => import("@/components/ReminderNotifications"));
const DigitalSignatures = ReactLazyLoading(() => import("@/components/DigitalSignatures"));
const ProjectReports = ReactLazyLoading(() => import("@/components/ProjectReports"));
const GjuPipelines = ReactLazyLoading(() => import("@/customers/gju-demo/pipelines"));

export default function RRoutes() {
  const session = useAppSelector(sessionSelector);
  const { currentCustomer, currentProject } = session;
  const { customer, project } = useParams<"customer" | "project">();

  const client = useApolloClient();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (customer !== currentCustomer.identifier || project !== currentProject.identifier) {
      (async () => {
        const { data } = await client.query({
          query: SessionCustomerDocument,
          variables: { identifier: customer },
        });
        if (!data?.customer) {
          throw new Error("could not find customer!");
        }
        const newProject = data.customer.projects.find((p) => p.identifier === project);

        if (newProject) {
          dispatch(setCurrentCustomerAndProject({ customer: data.customer, project: newProject }));
        } else {
          throw new Error("could not find project!");
        }
      })();
    }
  }, [customer, project, currentCustomer, currentProject, client, dispatch]);

  if (customer !== currentCustomer.identifier || project !== currentProject.identifier) {
    return <Loading absolute fullscreen translucent backdrop />;
  }

  return (
    <Routes>
      <Route path="profile/*" element={<ProfileInterface />} />
      <Route path="admin/*" element={<AdminInterface />} />
      <Route path="contacts/*" element={<ContactsInterface />} />
      {isGJUDemo(session) && <Route path="pipelines/*" element={<GjuPipelines />} />}
      <Route path="mails/*" element={<MailsInterface />} />
      <Route path="todos/*" element={<TodosInterface />} />
      <Route path="customer-contacts/*" element={<CustomerContactsInterface />} />
      <Route path="jobs/*" element={<JobsInterface />} />
      <Route path="calendar/*" element={<CalendarInterface />} />
      <Route path="stats/*" element={<StatsInterface />} />
      <Route path="invoices/*" element={<InvoicesInterface />} />
      <Route path="project/*" element={<ProjectInterface />} />
      <Route path="knowledgebase/*" element={<KnowledgebaseInterface />} />
      <Route path="knowledgebase-ng/*" element={<KnowledgebaseNgInterface />} />
      <Route path="messages/*" element={<MessagesInterface />} />
      <Route path="staff/*" element={<StaffInterface />} />
      <Route path="roster/*" element={<RosterInterface />} />
      <Route path="documents/*" element={<DocumentsInterface />} />
      <Route path="acd/*" element={<AcdInterface />} />
      <Route path="tickets/*" element={<TicketsInterface />} />
      <Route path="punchlist/*" element={<PunchlistInterface />} />
      <Route path="inventory/*" element={<InventoryInterface />} />
      <Route path="customer-feedbacks/*" element={<CustomerFeedbackInterface />} />
      <Route path="hotseat/*" element={<HotSeatInterface />} />
      <Route path="project-news/*" element={<ProjectNewsInterface />} />
      <Route path="file-exchange/*" element={<FileExchange />} />
      <Route path="reminder-notifications/*" element={<ReminderNotifications />} />
      <Route path="digital-signatures/*" element={<DigitalSignatures />} />
      <Route path="project-reports/*" element={<ProjectReports />} />

      <Route path="/*" element={<Main />} />
    </Routes>
  );
}
